/*
  Dialog.jsx
*/

// Npm.
import React, { useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

// Components.
import IconButton from '../IconButton';

// Icons.
import CloseIcon from '../../icons/Close';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    zIndex: 1300,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    outline: 'none',  // Stops focus effect on page load when first opened.
    '@media print': {
      //position: 'absolute !important',
    },
  },
  backdrop: {
    position: 'fixed',
    zIndex: -1,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  scroll: {
    outline: 'none',
    overflow: 'hidden auto',
    textAlign: 'center',
    height: '100%',
    '@media print': {
      //height: 'auto',
    },
    '&::after': {
      width: '0px',
      height: '100%',
      verticalAlign: 'middle',
      display: 'inline-block',
      content: '""',
    },
  },
  content: {
    margin: theme.space(4),
    position: 'relative',
    overflowY: 'auto',
    display: 'inline-block',
    verticalAlign: 'middle',
    textAlign: 'left',
    maxWidth: theme.xs,
    backgroundColor: theme.color_paper,
    boxShadow: theme.shadow(24),
    borderRadius: 8,
    padding: theme.space(2, 3),
    '&.fullWidth': {
      width: `calc(100% - ${theme.space(8)})`,
    },
    '&.top': {
      verticalAlign: 'top',
    }
  },
  title: {
    paddingBottom: theme.space(2),
  },
  closeButton: {
    position: 'absolute',
    zIndex: 1,
    top: theme.space(1),
    right: theme.space(1),
  }
}));

/**
 * Gets the target to render the dialog to.
 */
function getTarget() {
  return document.getElementById('botech-editor-container') || document.body;
}

/**
 * Helper to check if an element is inside a parent element, and if it is,
 * if it's one of the first or last focussable elements, meaning it needs to cycle focus.
 */
function doCycleFocusInDialog(parentEl, event) {

  const childEl = event.target;

  if (parentEl !== childEl && !parentEl.contains(childEl)) {
    event.preventDefault();
    parentEl.focus();
    return;
  }

}

/**
 * Renders a Dialog.
 */
export default function Dialog({
  
  // Dialog props.
  title,
  open,
  onClose,
  fullWidth,
  align,
  keepMounted = false,
  disableEscapeButton = false,

  // Other props.
  className,
  children,
  ...rest

}) {

  // Classes.

  const classes = useStyles();

  let rootClass = 'Dialog';
  let contentClass = classes.content;

  if (fullWidth) {
    contentClass += ' fullWidth';
  }

  if (align === 'middle' || align === 'top') {
    contentClass += ` ${align}`;
  }

  const cls = useMemo(() => cx(rootClass, classes.root, className), [rootClass, classes.root, className]);

  // Handler for pressing TAB key to navigate.
  // While the dialog is open, tab focus should not leave the dialog.

  useEffect(() => {
    if (open) {

      let timer = null;

      function handleKeyDown(event) {
        if (event.key === 'Tab') {
          const shiftKey = event.shiftKey;
          timer = setTimeout(() => {
            if (rootRef.current) {
              if (document.activeElement && document.activeElement !== rootRef.current && !rootRef.current.contains(document.activeElement)) {
                if (shiftKey) {
                  document.activeElement.blur();
                  document.documentElement.focus();
                } else {
                  rootRef.current.focus();
                }
              }
            }
          }, 1);  // Setting to 0 will make "document.activeElement" the previous element.
        }
      }

      document.addEventListener('keydown', handleKeyDown, false);

      return () => {
        if (timer != null) {
          clearTimeout(timer);
        }
        document.removeEventListener('keydown', handleKeyDown, false);
      };
      
    }
  }, [open]);

  // Effects to close when Escape is pressed.
  // Requires focus somewhere on the Dialog.
  // NOTE: "defaultPrevented" checked to see if this should close or not.
  // Not that reliable but stopPropagation will not work from Autocomplete.

  useEffect(() => {
    if (open && onClose && !disableEscapeButton) {

      function handleKeyDown(event) {
        if (event.key === 'Escape' && !event.defaultPrevented) {
          event.preventDefault();
          onClose();
        }
      }

      document.addEventListener('keydown', handleKeyDown, false);

      return () => {
        document.removeEventListener('keydown', handleKeyDown, false);
      };

    }
  }, [open, onClose, disableEscapeButton]);

  // When the dialog opens, we focus on the outside div to maintain tabbing order, and so that
  // something in the window is actually focussed so that the above "keydown" listener works.

  const rootRef = useRef();

  useEffect(() => {
    if (open && rootRef.current) {
      rootRef.current.focus();
    }
  }, [open]);

  // Handlers for clicking the "backdrop".
  // We don't want to close the dialog when clicking the dialog content.
  // Make sure the event starts and ends on the same DOM element.

  const backdropClick = useRef();

  function handleMouseDown(event) {
    backdropClick.current = event.target === event.currentTarget;
  }

  function handleClickBackdrop(event) {
    if (!backdropClick.current) {
      return;
    }
    backdropClick.current = null;
    if (onClose) {
      onClose();
    }
  }

  // Don't render if closed.

  if (!keepMounted && !open) {
    return null;
  }

  return ReactDOM.createPortal((
    <div
      ref={rootRef}
      className={cls}
      tabIndex={-1}
      role="presentation"
      onClick={handleClickBackdrop}
      hidden={!open}
      {...rest}
    >
      <div
        className={`${classes.backdrop} fadeIn`}
        aria-hidden="true"
      />
      <div
        className={classes.scroll}
        onMouseDown={handleMouseDown}
        role="presentation"
      >
        <div className={contentClass}>
          {
            Boolean(title) &&
            <h2 className={`${classes.title} h3`}>{ title }</h2>
          }
          <div>
            { children }
          </div>
          {
            Boolean(onClose) &&
            <IconButton
              className={classes.closeButton}
              onClick={onClose}
              title="Close"
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          }
        </div>
      </div>
    </div>
  ), getTarget());
}
